import { GridActionsCellItem } from "@mui/x-data-grid";
import { useState, useEffect, useRef } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { GoUpload } from "react-icons/go";
import { BsDownload } from "react-icons/bs";
import { Upload, ChevronDown } from "lucide-react";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { LoanDto, REPAYMENT_TYPE } from "../../../../models/loan.dto";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetLoan, useDeleteLoan } from "../../../../services/loan.service";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import LoanModalForm from "../modals/LoanModalForm";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import {
	useCheckUserPermissions,
	PermissionChecker,
	PERMISSIONS,
} from "../../../../helpers/usePermissionManager";
import UploadLoans from "../modals/UploadLoans";
import ExportLoansModal from "../modals/ExportLoansModal";
import { loansColumns, loansColumnVisibility } from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../models/table-configuration.dto";
import useUserStore from "../../../../state-management/useUserStore";

const LoansView = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const { currentUser, userType } = useUserStore();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const [openUploadLoansModal, setOpenUploadLoansModal] = useState(false);
	const [openExportLoansModal, setOpenExportLoansModal] = useState(false);
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(loansColumnVisibility);

	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<LoanDto>({
		fetchHook: useGetLoan,
		options: { companyId: currentCompany?.id },
	});

	// Fetched data for select fields requiring data from database
	const { data: payPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteLoan } = useDeleteLoan();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteLoan(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	return (
		<div>
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					{/* <APISearchInput 
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchLoans}
					/> */}
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{ title: "Date Created", fieldName: "createdAt" },
									{ title: "Date Modified", fieldName: "modifiedAt" },
								],
							},
							{
								title: "Employee",
								fieldName: "employeeId",
								fieldType: "SELECT_FIELD",
								options: employees?.data || [],
								mapTexts: ["firstName", "lastName"],
							},
							{
								title: "Start Pay Period",
								fieldName: "startPayPeriodId",
								fieldType: "SELECT_FIELD",
								options: payPeriods?.data || [],
								mapText: "code",
							},
							{
								title: "Repayment Type",
								fieldName: "repaymentType",
								fieldType: "CHECKBOX_LIST",
								options: REPAYMENT_TYPE,
							},
						]}
					/>
					<ManageTableColumns
						columns={loansColumns}
						defaultVisibilityModel={loansColumnVisibility}
						visibilityModel={columnVisibilityModel}
						setVisibilityModel={setColumnVisibilityModel}
						tableApiRef={tableAPIRef}
					/>
				</div>
				<div className="flex items-center gap-4 font-medium">
					<div className="relative" ref={dropdownRef}>
						<button
							onClick={() => setIsOpen(!isOpen)}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<Upload className="mr-3 text-[17px] text-gray-500" />
							Upload/Export
							<ChevronDown
								className={`ml-2 text-[14px] text-gray-500 transition-transform duration-200 ${
									isOpen ? "rotate-180" : ""
								}`}
							/>
						</button>

						{isOpen && (
							<div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-200 rounded-xl shadow-lg z-50">
								<div className="p-2 space-y-2">
									<PermissionChecker
										permissionRule="OR"
										permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
									>
										<button
											onClick={() => {
												setIsOpen(false);
												setOpenUploadLoansModal(true);
											}}
											className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
                   border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
										>
											<GoUpload className="text-[18px] text-gray-500" />
											Upload Loans
										</button>
									</PermissionChecker>
									<button
										onClick={() => {
											setIsOpen(false);
											setOpenExportLoansModal(true);
										}}
										className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
                 border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
									>
										<BsDownload className="text-[18px] text-gray-500" />
										Export Loans
									</button>
								</div>
							</div>
						)}
					</div>

					<PermissionChecker permissions={[PERMISSIONS.LOANS_WRITE]}>
						<div
							className="flex items-center text-primary-blue cursor-pointer"
							onClick={handleOpenModal}
						>
							<AiOutlinePlus className="mr-1 text-base" />
							<span className="text-sm">Add New Loan</span>
						</div>
					</PermissionChecker>
				</div>
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.LOANS}
				data={tableData}
				columns={loansColumns}
				columnVisibility={columnVisibilityModel}
				onColumnVisibilityChange={setColumnVisibilityModel}
				loading={isLoading}
				onRowClick={
					checkUserPermissionsAnd([PERMISSIONS.LOANS_WRITE])
						? (row, event) => {
								setFormData(row);
								setAnchorEl(event.currentTarget);
							}
						: undefined
				}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={loansColumnVisibility}
				pageSize={10}
				hasPermission={checkUserPermissionsAnd([PERMISSIONS.LOANS_READ])}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<LoanModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditForm ? "Update Loan" : "Add New Loan"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					payPeriods={payPeriods?.data || []}
					employees={employees?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadLoansModal && (
				<UploadLoans
					open={openUploadLoansModal}
					setOpen={setOpenUploadLoansModal}
				/>
			)}
			{openExportLoansModal && currentCompany?.id && (
				<ExportLoansModal
					open={openExportLoansModal}
					setOpen={setOpenExportLoansModal}
					companyId={currentCompany.id}
				/>
			)}
		</div>
	);
};

export default LoansView;
